/**
 * Admin API hooks and utilities
 * Handles secure session-based authentication for admin operations
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Make an authenticated admin API request
 * Uses credentials: 'include' to send session cookie
 */
export const adminFetch = async (endpoint, options = {}) => {
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  };

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...options.headers }
  });

  return response;
};

/**
 * Verify admin session is valid
 */
export const verifyAdminSession = async () => {
  try {
    const response = await adminFetch('/api/admin/verify');
    return response.ok;
  } catch {
    // Network error or server unavailable - treat as not authenticated
    return false;
  }
};

/**
 * Admin logout
 */
export const adminLogout = async () => {
  try {
    await adminFetch('/api/admin/logout', { method: 'POST' });
  } catch {
    // Server logout failed - proceed with local cleanup anyway
    if (process.env.NODE_ENV === 'development') {
      console.warn('Admin logout request failed; clearing local session anyway.');
    }
  }
};

/**
 * Fetch admin stats
 */
export const fetchAdminStats = async () => {
  const response = await adminFetch('/api/admin/stats');
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
};

/**
 * Fetch quotes/leads
 */
export const fetchQuotes = async (filters = {}) => {
  const params = new URLSearchParams();
  params.set('limit', '200');
  if (filters.status) params.set('status', filters.status);
  if (filters.trade_type) params.set('trade_type', filters.trade_type);
  
  const response = await adminFetch(`/api/admin/quotes?${params}`);
  if (!response.ok) throw new Error('Failed to fetch quotes');
  return response.json();
};

/**
 * Update a quote
 */
export const updateQuote = async (quoteId, updates) => {
  const response = await adminFetch(`/api/admin/quotes/${quoteId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
  if (!response.ok) throw new Error('Failed to update quote');
  return response.json();
};

/**
 * Delete a quote
 */
export const deleteQuote = async (quoteId) => {
  const response = await adminFetch(`/api/admin/quotes/${quoteId}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to delete quote');
  return response.json();
};

/**
 * Fetch application stats
 */
export const fetchApplicationStats = async () => {
  const response = await adminFetch('/api/admin/application-stats');
  if (!response.ok) throw new Error('Failed to fetch application stats');
  return response.json();
};

/**
 * Fetch applications
 */
export const fetchApplications = async (filters = {}) => {
  const params = new URLSearchParams();
  params.set('limit', '200');
  if (filters.status) params.set('status', filters.status);
  
  const response = await adminFetch(`/api/admin/applications?${params}`);
  if (!response.ok) throw new Error('Failed to fetch applications');
  return response.json();
};

/**
 * Update an application
 */
export const updateApplication = async (appId, updates) => {
  const response = await adminFetch(`/api/admin/applications/${appId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  });
  if (!response.ok) throw new Error('Failed to update application');
  return response.json();
};

/**
 * Delete an application
 */
export const deleteApplication = async (appId) => {
  const response = await adminFetch(`/api/admin/applications/${appId}`, {
    method: 'DELETE'
  });
  if (!response.ok) throw new Error('Failed to delete application');
  return response.json();
};
